import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { RootState } from "../../state/store";
import { useSignalRContext } from "../Contexts/SignalRContext";
import { useOnlineStatus } from "../Hooks/useOnlineStatus";
import { resolveConnectionIssueKind, hasRecovered, ConnectionIssueKind } from "../../utilities/helpers/connectionStatus";

export function ConnectionBanner() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { connection, connectionError, isReconnecting } = useSignalRContext();
  const isAuthenticated = useSelector((state: RootState) => state.authReducer.isAuthenticated);
  const isOnline = useOnlineStatus();

  const snackbarId = useRef<string | number | null>(null);
  const issueKindRef = useRef<ConnectionIssueKind>(null);
  const hasEverConnected = useRef(false);

  useEffect(() => {
    if (connection) hasEverConnected.current = true;

    const connectionIssueKind = resolveConnectionIssueKind({
      isOnline,
      isAuthenticated,
      isReconnecting,
      hasConnectionError: !!connectionError,
      hasLiveConnection: !!connection,
      hasEverConnected: hasEverConnected.current,
    });

    if (connectionIssueKind) {
      if (snackbarId.current === null || issueKindRef.current !== connectionIssueKind) {
        if (snackbarId.current !== null) closeSnackbar(snackbarId.current);
        snackbarId.current = enqueueSnackbar(
          <div>
            Connection lost. Reconnecting
            <FontAwesomeIcon className="ml-2" icon={faSpinner} spin />
          </div>,
          {
            variant: "warning",
            anchorOrigin: { vertical: "top", horizontal: "center" },
            persist: true,
            preventDuplicate: true,
          }
        );
        issueKindRef.current = connectionIssueKind;
      }
      return;
    }

    if (snackbarId.current === null) return;

    if (!isOnline) {
      closeSnackbar(snackbarId.current);
      snackbarId.current = null;
      issueKindRef.current = null;
      return;
    }

    if (hasRecovered({ isOnline, isAuthenticated, hasLiveConnection: !!connection })) {
      closeSnackbar(snackbarId.current);
      snackbarId.current = null;
      issueKindRef.current = null;
      enqueueSnackbar("Connection restored", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
        autoHideDuration: 3000,
      });
    }
  }, [connection, connectionError, isReconnecting, isOnline, isAuthenticated, enqueueSnackbar, closeSnackbar]);

  return null;
}

export default ConnectionBanner;
